import { Button } from "@/components/ui/button";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Copy,
    Layers,
    Bold,
    Italic,
    FileInput,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./text-element-editor.module.css";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { TextItem } from "@exibra/shared";

// Common web-safe fonts that work well for displays
const AVAILABLE_FONTS = [
    { value: "Arial", label: "Arial" },
    { value: "Helvetica", label: "Helvetica" },
    { value: "Verdana", label: "Verdana" },
    { value: "Tahoma", label: "Tahoma" },
    { value: "Trebuchet MS", label: "Trebuchet MS" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Georgia", label: "Georgia" },
    { value: "Garamond", label: "Garamond" },
    { value: "Courier New", label: "Courier New" },
    { value: "Brush Script MT", label: "Brush Script MT" },
];

/**
 * Props for the TextActions component
 */
interface TextActionsProps {
    /** The text element to edit */
    element: TextItem;
    /** Callback to update the style of the text element */
    onChange: (element: TextItem) => void;
    /** Callback to duplicate the text element */
    onDuplicate: (element: TextItem) => void;
}

/**
 * Renders the edit actions for a text element
 */
export const TextActions: React.FC<TextActionsProps> = ({ element, onDuplicate, onChange }) => {
    const [t] = useTranslation();
    const [fontSize, setFontSize] = useState(element.style.fontSize?.toString() || "");

    useEffect(() => {
        setFontSize(element.style.fontSize?.toString() || "");
    }, [element.style.fontSize]);

    const handleFontSizeChange = (value: string) => {
        setFontSize(value);
        const numValue = parseInt(value);
        if (!isNaN(numValue)) {
            onChange({ ...element, style: { ...element.style, fontSize: numValue } });
        }
    };

    const handleAlignment = (alignment: "left" | "center" | "right") => {
        onChange({ ...element, style: { ...element.style, align: alignment } });
    };

    const handleFontChange = (font: string) => {
        onChange({ ...element, style: { ...element.style, font } });
    };

    const toggleBold = () => {
        onChange({ ...element, style: { ...element.style, bold: !element.style.bold } });
    };

    const toggleItalic = () => {
        onChange({ ...element, style: { ...element.style, italic: !element.style.italic } });
    };

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => onDuplicate(element)}
                tooltip={t("templates.editor.layer.duplicate")}
            >
                <Copy size={12} />
            </Button>
            <div className={styles.separator}></div>

            <Button
                variant={element.isPlaceholder ? "default" : "ghost"}
                size="sm"
                onClick={() => onChange({ ...element, isPlaceholder: !element.isPlaceholder })}
                tooltip={t("templates.editor.text.placeholder")}
                className="w-8 h-8 p-0"
            >
                <FileInput size={12} />
            </Button>
            <div className={styles.separator}></div>

            <Select value={element.style.font || "Arial"} onValueChange={handleFontChange}>
                <SelectTrigger className="w-[140px] h-8">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {AVAILABLE_FONTS.map((font) => (
                        <SelectItem key={font.value} value={font.value}>
                            <span style={{ fontFamily: font.value }}>{font.label}</span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <div className={styles.separator}></div>

            <Button
                variant={element.style.bold ? "default" : "ghost"}
                size="sm"
                onClick={toggleBold}
                className="w-8 h-8 p-0"
            >
                <Bold size={12} />
            </Button>

            <Button
                variant={element.style.italic ? "default" : "ghost"}
                size="sm"
                onClick={toggleItalic}
                className="w-8 h-8 p-0"
            >
                <Italic size={12} />
            </Button>

            <div className={styles.separator}></div>

            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        tooltip={t("templates.editor.layer.positioning.tooltipTitle")}
                    >
                        <Layers size={12} />
                    </Button>
                </PopoverTrigger>
                <PopoverContent side="bottom" sideOffset={16} className="p-2">
                    <div className="flex flex-col gap-2">
                        <p className="text-sm font-bold px-4 pt-2">
                            {t("templates.editor.layer.positioning.title")}
                        </p>

                        <div className="flex items-center gap-2">
                            <Button
                                variant={element.style.align === "left" ? "default" : "ghost"}
                                className="flex-1 gap-4 justify-start"
                                onClick={() => handleAlignment("left")}
                            >
                                <AlignLeft size={12} />
                                <p className="text-sm">
                                    {t("templates.editor.layer.positioning.alignLeft")}
                                </p>
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant={element.style.align === "center" ? "default" : "ghost"}
                                className="flex-1 gap-4 justify-start"
                                onClick={() => handleAlignment("center")}
                            >
                                <AlignCenter size={12} />
                                <p className="text-sm">
                                    {t("templates.editor.layer.positioning.alignCenter")}
                                </p>
                            </Button>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant={element.style.align === "right" ? "default" : "ghost"}
                                className="flex-1 gap-4 justify-start"
                                onClick={() => handleAlignment("right")}
                            >
                                <AlignRight size={12} />
                                <p className="text-sm">
                                    {t("templates.editor.layer.positioning.alignRight")}
                                </p>
                            </Button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
            <div className={styles.separator}></div>
            <div className="flex items-center gap-2 px-2">
                {t("templates.editor.text.size")}
                <input
                    type="number"
                    value={fontSize}
                    onChange={(e) => handleFontSizeChange(e.target.value)}
                    className={styles.sizeInput}
                    min="1"
                />
                {"px"}
            </div>
        </>
    );
};
